/****************************************************
 * Module Name:  stoneWiseBidPreview.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import { Box, Tooltip, Typography, ThemeProvider, createTheme, Stack, Button, Badge } from "@mui/material";
import React, { useState, useEffect } from "react";
import { DataGridPro, GridEditInputCell, GridToolbar } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Select from "react-select";
import { LoadingButton } from "@mui/lab";
import Constants from "../../../utility/constants";
import TenderLotProxy from "../../../utility/proxy/manager/tender/tenderLotProxy";
import { BiSearchAlt2 } from "react-icons/bi";
import renderCellExpand from "../../common/dataGridCellExpand";
import Utility from "../../../utility/utility";
import NoPermission from "../../common/noPermission";
import EditIcon from '@mui/icons-material/Edit';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TenderLotBidProxy from "../../../utility/proxy/manager/tender/tenderLotBidProxy";
import { IoIosAddCircleOutline } from "react-icons/io";
import { MdOutlineAddPhotoAlternate } from "react-icons/md";
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import ViewImageModal from "./viewImageModal";
import AddTenderStoneRemarkModal from "./addTenderStoneRemarkModal";
import UploadTenderImagesModal from "./uploadTenderImagesModal";
import TabMenuSnackbar from "../../common/tabMenuSnackbar";
import TenderStoneDetailPreviewModal from "./tenderStoneDetailPreviewModal";
import VisibilityIcon from '@mui/icons-material/Visibility';
import logo from '../../../images/pansuriya.jpg'
import { Modal } from "react-bootstrap";
import PageTitle from "../../common/pageTitle";

const LotWiseBidPreview = () => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [companyTenderOptions, setCompanyTenderOptions] = useState([]);
    const [companyTender, setCompanyTender] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [salesBidLoader, setSalesBidLoader] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [outerLoader, setOuterLoader] = useState();
    const [outerRows, setOuterRows] = useState([]);

    const [filterValues, setFilterValues] = useState({});
    const [CompanyDataLoader, setCompanyDataLoader] = useState(true);
    const [selectionModel, setSelectionModel] = useState([]);
    const [lockLoader, setLockLoader] = useState(false);

    const [remark, setRemark] = useState("");
    const [showUploadImgsModal, setShowUploadImgsModal] = useState(false);
    const [showAddRemarkModal, setShowAddRemarkModal] = useState(false);
    const [showViewImageModal, setShowViewImageModal] = useState(false);
    const [loader, setLoader] = useState(false);
    const [tenderStoneId, setTenderStoneId] = useState("");
    const [showTenderStoneDetailModal, setShowTenderStoneDetailModal] = useState(false);
    const [columnNames, setColumnNames] = React.useState([]);

    const [lotData, setLotData] = React.useState({});
    const [showStoneDataModal, setShowStoneDataModal] = React.useState(false); // to show data after view button clicked

    const local_Permission = Constants.USER_PERMISSION.filter(
        (iPermission) => iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_LOT);

    useEffect(() => {
        PageTitle("TENDER_OPERATION_BID_PREVIEW_LOT");
        checkPermission();
    }, []);

    const checkPermission = () => {
        if (Utility.isReadAllowed(local_Permission)) {
            getCompanyTender();
        }
    }

    const getCompanyTender = () => {

        setOuterRows([]);
        setCompanyDataLoader(true);
        setCompanyTenderOptions([]);
        setCompanyTender("");
        let optionsArray = [];
        TenderLotProxy.GetCompanyTendersForLotBid(true, "LOCKED", "COMPLETED", (response) => {
            if (response) {
                response.map((data) => {
                    let option = { value: data.tenderid, label: data.companyname + " | " + (data.tendername === null ? " - " : data.tendername), companyid: data.companyid };
                    optionsArray.push(option);
                    return true;
                });
                setCompanyDataLoader(false);
            } else {
                setCompanyDataLoader(false);
            }
        })
        setCompanyTenderOptions(optionsArray);
    }

    const handleSearch = () => {
        // need to fetch such companies and tenders which are not yet locked 
        // so both stone wise bid and lot wise bid status should be "IN_PROGRESS"
        setOuterRows([]);
        setSalesBidLoader(true);
        TenderLotProxy.GetCompletedLotData(companyTender.value, companyTender.companyid, true, "LOCKED", "COMPLETED", (response) => {
            if (response && Array.isArray(response)) {

                const uniqueFilterValues = {
                    tendername: Utility.getDistinctOptions(response, "tendername"),
                    minename: Utility.getDistinctOptions(response, "minename"),
                };

                prepareColumns(uniqueFilterValues);
                setFilterValues(uniqueFilterValues);
                setSalesBidLoader(false);
                setOuterLoader(false);
                let tempResponse = response.map(iData => {
                    iData.editable = (iData.basicamount && iData.basicamount !== 0.00) ? true : false;
                    iData.discountonpropsedbidamt = null;
                    iData.tenderstonelist.forEach(irow => {
                        irow.price = null;
                        if (irow.amount && irow.size) {
                            irow.price = parseFloat((irow.amount / irow.size).toFixed(2));
                        }
                    })
                    return iData;
                });
                setOuterRows(tempResponse);
            } else {
                setShowSnackbar(true);
                setSalesBidLoader(false);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    };

    const prepareColumns = (uniqueFilterValues) => {
        const columnNames = columnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "tenderlotid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_LOT_LOTCODE];
                    break;
                case "mineid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_MINE_NAME];
                    break;
                case "tendername":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_PRICE,
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_AMOUNT
                    ];
                    break;
                case "size":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_LOT_PROPOSED_PPC,
                        Constants.COLUMN_PERMISSION.MFG_LOT_PROPOSED_DISCOUNT,
                        Constants.COLUMN_PERMISSION.MFG_LOT_PROPOSED_AMOUNT,
                    ];
                    break;
                case "bidppc":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_LOT_LESS_BID_PERCENT,
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, columnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        // Update state with the prepared columns
        setColumnNames(columnNames);
    };

    const prepareInnerColumns = (uniqueFilterValues) => {

        const columnNames = innercolumnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "size":
                    guids = [Constants.COLUMN_PERMISSION.MFG_LOT_STONE_SIZE_PERCENT, Constants.COLUMN_PERMISSION.MFG_STONE_PIECES];
                    break;
                case "id":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_STONE_LOCKED_PLAN_ID,
                        Constants.COLUMN_PERMISSION.MFG_STONE_ISDIRECTLOCKED,
                        Constants.COLUMN_PERMISSION.MFG_STONE_PPC,
                        Constants.COLUMN_PERMISSION.MFG_STONE_AMOUNT,
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, innercolumnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array


        return columnNames;
    };

    const fetchColumnDataFromGuid = (guids, currentColumn, columnsWithPermission, uniqueFilterValues) => {
        const permissionColumns = guids
            .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
            .filter(Boolean) // Remove undefined values (invalid permissions)
            .map(guid => {
                const columnData = columnsWithPermission[guid]; // Get column details

                // If the columnData is missing or an empty object, skip adding it
                if (!columnData || Object.keys(columnData).length === 0) {
                    return null;
                }

                // Retrieve unique filter values for the column, defaulting to an empty array
                const filterData = uniqueFilterValues[columnData.field] || [];

                return {
                    ...columnData,  // Copy all existing column properties
                    valueOptions: filterData,  // Assign filter values
                };
            })
            .filter(Boolean); // Remove any null values from the list

        // Modify the original column before adding it to the list
        const modifiedColumn = {
            ...currentColumn,  // Retain original column properties
            valueOptions: uniqueFilterValues[currentColumn.field] || [], // Assign filter values
        };

        // Return an array with the modified column and any valid permission columns
        return [modifiedColumn, ...permissionColumns];
    };

    const lockBid = () => {

        let data = [];
        outerRows.forEach(iRow => {
            if (selectionModel.includes(iRow.tenderlotid)) {
                data.push({
                    tenderlotid: iRow.tenderlotid,
                    bidamount: iRow.bidamount,
                    proposedamount: iRow.proposedamount,
                    lockedby: Constants.loggedInUser.id,
                    proposeddiscount: iRow.proposeddiscount,
                    proposedppc: iRow.proposedppc,
                    lessbidpercent: iRow.lessbidpercent,
                    bidppc: iRow.bidppc,
                });
            }
        });

        if (data.length > 0) {
            setLockLoader(true);
            TenderLotBidProxy.LockLotWiseBid(data, (response) => {
                if (response && response.status === "Success") {
                    setLockLoader(false);
                    setShowSnackbar(true);
                    setSnackbarMessage("Locked Successfully");
                    setSnackbarVariant("success");
                    handleSearch();
                } else {
                    setLockLoader(false);
                    setShowSnackbar(true);
                    setSnackbarMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                }
            })
        }
    }

    const addRemark = (data) => {
        setLoader(true);
        TenderLotProxy.AddRemark(data, tenderStoneId, (response) => {
            if (response) {
                setShowSnackbar(true);
                setSnackbarMessage("Remark Added Successfully");
                setSnackbarVariant("success");
                setLoader(false);
                setTenderStoneId();
                setRemark("");
                handleSearch();
            } else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setLoader(false);
                setTenderStoneId();
                setRemark("");
            }
        });
    }

    const handleUpload = (data, tenderstoneid) => {
        setLoader(true);
        TenderLotProxy.UploadTenderStoneImages(data, tenderstoneid, (response) => {
            if (response.status === "Success") {
                setShowSnackbar(true);
                setSnackbarMessage(response.errorMessage)
                setSnackbarVariant("success");
                setLoader(false);
                setShowUploadImgsModal(false);
                setTenderStoneId();
                handleSearch();
            } else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setLoader(false);
                setShowUploadImgsModal(false);
                setTenderStoneId();
                handleSearch();
            }
        })
    }

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];
        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.tenderlotid,
                    field: field,
                    value: newRow[field]
                });
            }
        });

        return changedFields;
    };

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const processRowUpdate = (newR, oldRow) => {
        const changedFeilds = getChangedFields(newR, oldRow);

        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];
            let newRecord;
            let updatedRow = outerRows.map(iRow => {
                if (iRow.tenderlotid === id) {
                    if (field === "bidamount" && parseFloat(value) !== parseFloat(iRow.bidamount)) {
                        const bidamount = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                        const bidppc = parseFloat((bidamount / iRow.carats).toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                        let biddiscount = null;
                        if (iRow.basicamount && iRow.basicamount !== 0.00) {
                            biddiscount = (((bidamount - iRow.basicamount) / (iRow.basicamount)) * 100).toFixed(Constants.DEFAULT_NO_OF_DIGITS);
                        }

                        const newRow = { ...iRow, [field]: bidamount, bidppc: bidppc, lessbidpercent: biddiscount, discountonpropsedbidamt: 0 };
                        newRecord = newRow;
                        return newRow;
                    }
                    else if (field === "bidppc" && parseFloat(value) !== parseFloat(iRow.bidppc)) {
                        const bidppc = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                        const bidamount = parseFloat((bidppc * iRow.carats).toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                        let biddiscount = null;
                        if (iRow.basicamount && iRow.basicamount !== 0.00) {
                            biddiscount = (((bidamount - iRow.basicamount) / (iRow.basicamount)) * 100).toFixed(Constants.DEFAULT_NO_OF_DIGITS);
                        }

                        const newRow = { ...iRow, [field]: bidppc, bidamount: bidamount, lessbidpercent: biddiscount, discountonpropsedbidamt: 0 };
                        newRecord = newRow;
                        return newRow;
                    }
                    else if (field === "lessbidpercent" && parseFloat(value) !== parseFloat(iRow.lessbidpercent) && iRow.basicamount && iRow.basicamount !== 0.00) {
                        const biddiscount = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));

                        const bidamount = (iRow.basicamount + ((iRow.basicamount * biddiscount) / 100)).toFixed(Constants.DEFAULT_NO_OF_DIGITS);
                        const bidppc = parseFloat((bidamount / iRow.carats).toFixed(Constants.DEFAULT_NO_OF_DIGITS));


                        const newRow = { ...iRow, [field]: biddiscount, bidppc: bidppc, bidamount: bidamount, discountonpropsedbidamt: 0 };
                        newRecord = newRow;
                        return newRow;
                    } else if (field === "discountonpropsedbidamt" && parseFloat(value) !== parseFloat(iRow.discountonpropsedbidamt)) {

                        // ADDITIONAL DISCOUNT ON BID AMOUNT PROPOSED
                        const discountonpropsedbidamt = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));

                        const bidamount = (iRow.proposedamount + ((iRow.proposedamount * discountonpropsedbidamt) / 100)).toFixed(Constants.DEFAULT_NO_OF_DIGITS);

                        let biddiscount = null;
                        if (iRow.basicamount && iRow.basicamount !== 0.00) {
                            biddiscount = (((bidamount - iRow.basicamount) / (iRow.basicamount)) * 100).toFixed(Constants.DEFAULT_NO_OF_DIGITS);
                        }

                        const bidppc = parseFloat((bidamount / iRow.carats).toFixed(Constants.DEFAULT_NO_OF_DIGITS));


                        const newRow = { ...iRow, [field]: discountonpropsedbidamt, bidppc: bidppc, bidamount: bidamount, lessbidpercent: biddiscount };
                        newRecord = newRow;
                        return newRow;
                    }
                }
                return iRow;
            });
            setOuterRows(updatedRow);
            return newRecord;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newR };
        }
    }

    const columnsWithPermission = {
        "eabe76e1-e41b-40ef-b9ae-cbd13293bd1e": {
            field: "lotcode",
            headerName: "LOT CODE",
            headerAlign: "center",
            align: "center",
            width: 78,
            renderCell: renderCellExpand,
            filterOperators: Utility.defaultFilters(),
            sortComparator: (v1, v2) => Utility.sortAlphaNumeric(v1, v2)
        },
        "4a1de405-1802-4664-85e2-6ff7cf8c3bba": {
            field: "minename",
            headerName: "MINE NAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "3634473f-45cf-4def-8e69-c1f34ebe0f18": {
            field: "basicprice",
            headerName: "BASIC PRICE",
            headerAlign: "center",
            align: "center",
            width: 94,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
        "394908cf-dc48-40ee-b68f-23efa651806e": {
            field: "basicamount",
            headerName: "BASIC AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
        "0256efa0-1afb-495b-9b4c-8e315f657de1": {
            field: 'proposedppc',
            headerName: 'PROPOSED PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 120,
            cellClassName: (params) => 'blueCell',
        },
        "b8e19df2-1ed5-4c27-888c-d946eee13293": {
            field: 'proposeddiscount',
            headerName: 'PROPOSED DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => !params.value ? "-" : params.value,
            width: 110,
            cellClassName: (params) => 'blueCell',
        },
        "b48824d5-a8c6-4e7f-a0e4-ebcb4858d3be": {
            field: 'proposedamount',
            headerName: 'PROPOSED AMT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 120,
            cellClassName: (params) => 'blueCell',
        },
        "b3841f2e-2835-47e6-a929-d27a696762ad": {
            field: 'lessbidpercent',
            headerName: 'LESS BID DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderHeader: () => (
                <Tooltip title="DIS ON BASIC AMOUNT" placement="bottom">
                    <span>LESS BID DIS</span>
                </Tooltip>
            ),
            editable: (params) => params.row.editable,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : params.row.lessbidpercent === null ? "-" : parseFloat(params.row.lessbidpercent).toFixed(Constants.DEFAULT_NO_OF_DIGITS)}
                    </span>
                    {params.row.editable ?
                        <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                        : <div style={{ cursor: 'not-allowed' }} />}
                </div>
            ),
            renderEditCell: (params) => {
                return (
                    params.row.editable ?
                        <Box ><GridEditInputCell {...params} /></Box> :
                        null
                )
            },
            cellClassName: (params) => params.row.editable ? 'lavenderCell' : 'cursorNA',
            width: 95
        },
    };

    const columnsWithoutPermission = [
        {
            field: "",
            headerName: "",
            headerAlign: "center",
            align: "center",
            width: 70,
            renderCell: (params) => (
                <Button
                    sx={{
                        width: "44px !important",
                        height: "28px !important",
                        fontSize: "10px !important",
                        borderRadius: "12px !important",
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowStoneDataModal(true);
                        setLotData(params.row)
                    }}
                    component="label"
                    variant="contained"
                    size="small"
                >
                    VIEW
                </Button >
            ),
        },
        {
            field: 'tenderlotid',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 70
        },
        {
            field: 'tenderid',
            headerName: 'TENDER ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'tendername',
            headerName: 'TENDER',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.tendername : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 110
        },
        {
            field: 'carats',
            headerName: 'CTS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 76
        },
        {
            field: 'description',
            headerName: 'DESCRIPTION',
            headerAlign: 'center',
            align: 'center',
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'mineid',
            headerName: 'MINE ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'pcs',
            headerName: 'PCS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'size',
            headerName: 'SIZE',
            headerAlign: 'center',
            align: 'center',
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'bidppc',
            headerName: 'FINAL PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : "-"}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            width: 90,
            cellClassName: (params) => 'lavenderCell',
        },
        {
            field: 'discountonpropsedbidamt',
            headerName: 'DIS/ PROP AMT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            editable: true,
            renderHeader: () => (
                <Tooltip title="ADDITIONAL DIS ON PROPOSED AMOUNT" placement="bottom">
                    <span>DIS ON PROP AMT</span>
                </Tooltip>
            ),
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : 0.00}
                    </span>
                    <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                </div>
            ),
            cellClassName: 'lavenderCell',
            width: 125
        },
        {
            field: 'bidamount',
            headerName: 'FINAL AMT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : parseFloat(params.row.proposedamount).toFixed(Constants.DEFAULT_NO_OF_DIGITS)}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
            width: 110
        },
    ];

    const innercolumnsWithPermission = {
        "86504376-84c2-4f7c-9187-bdd52e129f79": {
            field: "stonesizepercent",
            headerName: "STONE SIZE %",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "c5e679a6-3245-44e7-aead-597217b1f8a5": {
            field: "planid",
            headerName: "LOCKED PLAN ID",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "fb2c4dea-ee87-40dc-8e5c-19545b341cfc": {
            field: "isdirectlocked",
            headerName: "DIRECT LOCKED",
            headerAlign: "center",
            align: "center",
            width: 118,
            type: "boolean",
        },
        "11412fd5-4a4a-4957-877b-daead050bbe4": {
            field: 'amount',
            headerName: 'AMOUNT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 125
        },
        "e6e417c3-e383-4da3-9315-50994c7e73bb": {
            field: 'price',
            headerName: 'PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 100
        },
        "9c0b6a70-9b92-4283-b27c-4e424c5d35ef": {
            field: "pieces",
            headerName: "PIECES",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
    };

    const innercolumnsWithoutPermission = [
        {
            field: 'id',
            headerName: 'TENDER STONE ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 138
        },
        {
            field: "planids",
            headerName: "PLAN IDS",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "view",
            headerName: "VIEW PLAN",
            headerAlign: "center",
            align: "center",
            width: 200,
            renderCell: (params) => {
                return (
                    (params.row.planid ? (<Button
                        component="label"
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon size={20} />}
                        onClick={() => {
                            setShowTenderStoneDetailModal(true);
                            setTenderStoneId(params.row.id);
                            setShowStoneDataModal(false);
                        }}
                    >
                        VIEW LOCKED PLAN
                    </Button>
                    ) : "-"))  // because it will be null if the stone is locked directly
            },
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "addremark",
            headerName: "ADD REMARK",
            headerAlign: "center",
            align: "center",
            width: 122,
            renderCell: (params) => {
                return (
                    <Button
                        component="label"
                        variant="contained"
                        size="small"
                        startIcon={<IoIosAddCircleOutline size={20} />}
                        onClick={() => {
                            setShowAddRemarkModal(true);
                            setTenderStoneId(params.row.id);
                            setRemark(params.row.remark)
                        }}
                    >
                        REMARK
                    </Button>
                )
            },
        },
        {
            field: "upload",
            headerName: "UPLOAD",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setTenderStoneId(params.row.id);
                            setShowUploadImgsModal(true);
                        }}
                    >
                        {" "}
                        <MdOutlineAddPhotoAlternate color="primary" size={25} />
                    </Box>
                )
            },
        },
        {
            field: "noofimages",
            headerName: "VIEW IMG",
            headerAlign: "center",
            align: "center",
            width: 85,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => {
                return params.row.noofimages && params.row.noofimages > 0 ? (
                    <Badge
                        badgeContent={params.row.noofimages}
                        color="primary"
                        overlap="circular"
                        sx={{
                            "& .MuiBadge-badge": {
                                fontSize: "0.7rem",
                                width: "15px",
                                height: "17px",
                                borderRadius: "50%",
                            }
                        }}
                    >
                        <VideoLibraryTwoToneIcon
                            sx={{ cursor: "pointer", fontSize: "25px" }}
                            onClick={() => {
                                setShowViewImageModal(true);
                                setTenderStoneId(params.row.id);
                            }}
                        />
                    </Badge>
                ) : (
                    <span style={{ color: "black", fontStyle: "italic" }}>No Media</span>
                );
            },
        },
        {
            field: 'lockedby',
            headerName: 'LOCKED BY',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'lockedat',
            headerName: 'LOCKED AT',
            headerAlign: 'center',
            align: 'center',
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            },
            width: 180
        },
        {
            field: 'lockedby',
            headerName: 'LOCKED BY',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
    ];

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback((row) => <DetailPanelContent row={row} />,
        [],);

    function DetailPanelContent({ row: rowProp, isModalView }) {

        const middlerows = rowProp.tenderstonelist;

        let sortedrows = [...middlerows];
        if (sortedrows.length > 0) {
            sortedrows = sortedrows.sort(
                (p1, p2) => (p1.id > p2.id) ? 1 : (p1.id < p2.id) ? -1 : 0);
        }

        let middlecolumns = prepareInnerColumns(filterValues);

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "87vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        },
                        height: isModalView ? '60vh' : "auto",
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={middlecolumns}
                        rows={sortedrows}
                        getRowId={(row) => row.id}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: isModalView ? '90vh' : '30vh',
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    lockedat: false,
                                    lockedbyusername: false,
                                    lockedby: false,
                                    status: false,
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }

    const defaultMaterialTheme = createTheme();

    return !Utility.isReadAllowed(local_Permission) ? <NoPermission /> : (
        <Box>

            {showSnackbar ? (
                <TabMenuSnackbar
                    show={true}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Failed" : "Warning"}
                    handleClose={() => setShowSnackbar(false)}
                />
            ) : null}

            {/* shows stone data of the lot which can also be accessed by "+" button */}
            {showStoneDataModal ? (
                <Modal
                    backdrop="static"
                    show={true}
                    onHide={() => setShowStoneDataModal(false)}
                    dialogClassName="max-modalSize"

                    enforceFocus={false}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;Lot Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <DetailPanelContent row={lotData} isModalView={true} />
                    </Modal.Body>
                </Modal>)
                : null}

            {/* shows palns data of the stone which can also be accessed by "view plan" button */}
            {showTenderStoneDetailModal ? (
                <TenderStoneDetailPreviewModal
                    tenderStoneId={tenderStoneId}
                    isStoneWise={false}
                    show={showTenderStoneDetailModal}
                    handleClose={() => {
                        setShowTenderStoneDetailModal(false)
                    }}
                />
            ) : null}

            {showUploadImgsModal ? (
                <UploadTenderImagesModal
                    show={showUploadImgsModal}
                    loader={loader}
                    handleSave={handleUpload}
                    handleClose={() => {
                        setShowUploadImgsModal(false);
                        setTenderStoneId();
                    }}
                    tenderstoneid={tenderStoneId}
                />
            ) : null}

            {showAddRemarkModal ? (
                <>
                    <AddTenderStoneRemarkModal
                        show={showAddRemarkModal}
                        remarkData={remark}
                        handleClose={() => setShowAddRemarkModal(false)}
                        handleSave={addRemark}
                    />
                </>
            ) : null}

            {showViewImageModal ? (
                <>
                    <ViewImageModal
                        show={showViewImageModal}
                        tenderstoneid={tenderStoneId}
                        handleClose={() => setShowViewImageModal(false)}
                    />
                </>
            ) : null}

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0" }}>

                <Box sx={{ width: "46%", display: "flex", alignItems: "center" }}>
                    {/* Company Tender */}
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "75%", paddingRight: "15px" }}>
                        <Typography sx={{ width: "25%" }}>Company Tender</Typography>
                        <Select menuPlacement="auto"
                            blurInputOnSelect={false}
                            menuPortalTarget={document.body}
                            styles={{
                                ...Constants.Styles,
                                control: (base) => ({
                                    ...base,
                                    width: '27rem', // Adjust the width as needed
                                    minHeight: "40px"
                                }),
                            }}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                            options={companyTenderOptions}
                            value={companyTender}
                            isClearable={true}
                            onChange={(e) => setCompanyTender(e)}
                        />
                        {CompanyDataLoader ?
                            (<Box sx={{ marginLeft: 2 }}>
                                {<CircularProgress size={20} />}
                            </Box>) :
                            (<Box sx={{ marginLeft: 2 }}>
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => getCompanyTender()}
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            </Box>)}
                    </Box>

                    {/* Submit */}
                    <Box sx={{
                        width: "17%",
                        "& .MuiButtonBase-root": {
                            minHeight: "40px",
                        },
                        "& .Mui-disabled": {
                            backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                            color: "rgba(0, 0, 0, 0.26) !important"
                        }
                    }}>
                        <LoadingButton
                            sx={{ mr: 1 }}
                            variant="contained"
                            color="info"
                            onClick={handleSearch}
                            loading={salesBidLoader}
                            loadingPosition="start"
                            startIcon={<BiSearchAlt2 />}
                            type="Submit"
                            disabled={!companyTender}
                        >
                            Search
                        </LoadingButton>
                    </Box>
                </Box>

            </Box>

            <ThemeProvider theme={defaultMaterialTheme}>
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        "& .blueCell": {
                            color: "#080808",
                            backgroundColor: "#B7ECEA",
                        },
                        height: "69vh",
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        }
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={columnNames}
                        rows={outerRows}
                        loading={outerLoader}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "4px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "12px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "18px",
                            },

                            "& .lavenderCell": {
                                color: "#080808",
                                backgroundColor: "#D8BFD8",
                            },
                            "& .blueCell": {
                                color: "#080808",
                                backgroundColor: "#B7ECEA",
                            },
                            "& .cursorNA": {
                                color: "#080808",
                                backgroundColor: "#D8BFD8",
                                cursor: "not-allowed",
                            },
                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                        }}
                        checkboxSelection={outerRows.length > 0}
                        onRowSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                        rowSelectionModel={selectionModel}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        getRowId={(row) => row.tenderlotid}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    tenderlotid: false,
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    lockedat: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false,
                                    tenderid: false,
                                    mineid: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        getDetailPanelHeight={getDetailPanelHeight}
                        getDetailPanelContent={outerRows.length === 0 ? null : ({ row }) => getDetailPanelContent(row)}
                        processRowUpdate={(newR, oldRow) => processRowUpdate(newR, oldRow)}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        apiRef={apiRef}
                        onCellClick={handleCellClick}
                        editMode="cell"
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: "3px",
                            right: "265px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            "& .MuiButtonBase-root": {
                                minHeight: "30px",
                            },
                            "& .Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important"
                            }
                        }}
                    >
                        {outerLoader ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={() => handleSearch()}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer" }}
                                />
                            </Tooltip>
                        )}

                        <Box sx={{
                            margin: "0px 8px",
                            "& .MuiLoadingButton-root": {
                                padding: "6px 12px",
                                margin: 0
                            }
                        }}>
                            <LoadingButton
                                variant="contained"
                                color="info"
                                onClick={e => lockBid()}
                                loading={lockLoader}
                                loadingPosition="start"
                                startIcon={<AssignmentTurnedInIcon />}
                                type="Submit"
                                disabled={selectionModel.length === 0}
                            >
                                LOCK
                            </LoadingButton>
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>

        </Box>
    )
}

export default LotWiseBidPreview;